import { useNavigate } from 'react-router-dom';
import { useAuth } from '../lib/auth';
import { KINGSTON, ALBANY, siteMeta } from '../lib/sites';

// In-app program picker (3CLHUB-style) for the Supabase build. After login,
// the app lands here so the user CHOOSES which program to open instead of
// being dropped straight into one. Mirrors the role→apps map + tiles from
// public/3c-hub.html, but routes within the React SPA.
//
// Tiles can now target a LOCATION as well as a program. A tile whose `site`
// differs from the active one switches location on the way in (full reload —
// see sites.js switchSite), so the shell always comes up already scoped.
//
// Two accounts rely on that:
//   owner — one 3CAL tile for full rein over Albany, per Ryder's call that the
//           owner picks the shop at the hub rather than toggling inside the app.
//   hd    — Ric holds ONE login with a different role at each shop, so he gets
//           two tiles: 3CAL as Head Detailer, 3CHA as Detailer. The hub IS his
//           profile switcher; he never sees the principal's location toggle.

const APPS = {
  '3cha':          { code: '3CHA', title: 'Shop Operations',      sub: 'Schedule, customers, stock, revenue' },
  '3cvip-service': { code: 'VIP',  title: '3CVIP Service',         sub: 'Walk-around capture · customer media' },
  '3cam':          { code: '3CAM', title: 'Accounting Management', sub: 'Income, expenses, cashflow, reports' },
  '3cma':          { code: '3cMA', title: 'Marketing',             sub: 'Ads strategy & marketing intelligence' },
  // --- 3CAL (Albany) ---
  '3cal':          { code: '3CAL', title: '3C Albany',             sub: 'Shop operations · Albany location', site: ALBANY, to: '/dashboard' },
  '3cal-hd':       { code: '3CAL', title: '3C Albany',   sub: 'Head Detailer · full shop floor', site: ALBANY,   to: '/dashboard' },
  // Ric's Kingston tile. Same full shop shell as his Albany tile (Ryder,
  // 2026-10-26: "both of his profiles … should be identical"); it used to open
  // the detailer shell, which has no location pill and stranded him there.
  '3cha-detailer': { code: '3CHA', title: '3C Kingston', sub: 'Head Detailer · full shop floor', site: KINGSTON, to: '/dashboard' },
};

function appsForRole(role) {
  if (role === 'owner') return ['3cha', '3cvip-service', '3cam', '3cma', '3cal'];
  if (role === 'principal') return ['3cha', '3cvip-service']; // Albany via the in-app toggle, not a tile
  if (role === 'hd') return ['3cal-hd', '3cha-detailer'];
  if (role === 'tax-advisor') return ['3cam']; // read-only accountant — accounting only
  return ['3cha'];
}

export default function HubPicker() {
  const { role, logout, site, goToSite } = useAuth();
  const navigate = useNavigate();

  function open(id) {
    const a = APPS[id];
    // Cross-location tile: persist the new site and hard-navigate, so the
    // Supabase client is rebuilt with the right x-3c-site header and no cached
    // read from the other shop can survive into the new shell.
    if (a && a.site && a.site !== site) { goToSite(a.site, { to: a.to || '/' }); return; }
    if (a && a.to) { navigate(a.to); return; }
    if (id === '3cha') navigate(role === 'detailer' ? '/detailer' : '/dashboard');
    else if (id === '3cam') navigate('/books');
    else if (id === '3cma') navigate('/marketing');
    // 🚨 Carry the shop. The capture page has no auth context of its own and
    // scopes every read by it; arriving with nothing meant Kingston's registry
    // for everybody, so an Albany customer could not be found at all.
    else if (id === '3cvip-service') window.location.href = '/3c-insurance.html?site=' + encodeURIComponent(site || '');
  }

  const tile = {
    display: 'flex', alignItems: 'center', gap: 14, padding: 18, width: '100%',
    background: 'var(--card, #14141c)', border: '1px solid var(--border, #2a2a35)',
    borderRadius: 12, cursor: 'pointer', textAlign: 'left', color: 'inherit',
    fontFamily: 'inherit', marginBottom: 12,
  };

  const ids = appsForRole(role);

  return (
    <div style={{ minHeight: '100dvh', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: 24, background: 'var(--bg, #0b0b10)', color: 'var(--text, #e8e8ef)', fontFamily: 'Inter, system-ui, sans-serif' }}>
      <div style={{ width: '100%', maxWidth: 420 }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 6 }}>
          <div style={{ fontSize: 22, fontWeight: 800, letterSpacing: '.5px' }}><span style={{ color: '#2563eb' }}>3</span>CLHUB</div>
          <button onClick={() => { try { logout(); } catch (_) {} window.location.replace('/'); }}
            style={{ background: 'transparent', border: '1px solid var(--border, #2a2a35)', color: 'var(--muted, #9aa0ac)', borderRadius: 8, padding: '5px 11px', fontSize: 12, cursor: 'pointer', fontFamily: 'inherit' }}>Sign out</button>
        </div>
        <div style={{ fontSize: 13, color: 'var(--muted, #9aa0ac)', marginBottom: 20 }}>
          Signed in as <span style={{ color: 'var(--text, #e8e8ef)', fontWeight: 600, textTransform: 'capitalize' }}>{role === 'hd' ? 'Head Detailer' : (role || '—')}</span>
          {' · '}{role === 'hd' ? 'choose a location' : 'choose a program'}
        </div>
        {ids.map((id) => {
          const a = APPS[id];
          const s = siteMeta(a.site || site);
          // Badge every tile with its location once this account can reach more
          // than one shop. A single-location user sees no badges at all, so the
          // hub is unchanged for the four Kingston-only accounts.
          const showSite = ids.some((o) => (APPS[o].site || site) !== (APPS[ids[0]].site || site));
          return (
            <button key={id} onClick={() => open(id)} style={tile}
              onMouseEnter={(e) => { e.currentTarget.style.borderColor = s.accent; }}
              onMouseLeave={(e) => { e.currentTarget.style.borderColor = 'var(--border, #2a2a35)'; }}>
              <div style={{ width: 44, height: 44, borderRadius: 10, background: `${s.accent}24`, color: s.accent, display: 'flex', alignItems: 'center', justifyContent: 'center', fontFamily: 'JetBrains Mono, monospace', fontWeight: 700, fontSize: 13, flexShrink: 0 }}>{a.code}</div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 14, fontWeight: 600 }}>{a.title}</div>
                <div style={{ fontSize: 12, color: 'var(--muted, #9aa0ac)', marginTop: 2 }}>{a.sub}</div>
              </div>
              {showSite && (
                <div style={{ fontFamily: 'JetBrains Mono, monospace', fontSize: 11, fontWeight: 700, color: s.accent, border: `1px solid ${s.accent}55`, borderRadius: 6, padding: '2px 6px' }}>{s.short}</div>
              )}
              <div style={{ color: 'var(--muted, #9aa0ac)', fontSize: 18 }}>›</div>
            </button>
          );
        })}
      </div>
    </div>
  );
}
